import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { getRouteApi, Outlet, useMatchRoute, useNavigate } from '@tanstack/react-router';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { cn } from '@nesso/ui/lib/utils';

import { isExpiredPaid, subscriptionMeOptions } from '@/entities/subscription';
import { AddClient } from '@/features/client/add-client';
import { AddClientContact } from '@/features/client/add-client-contact';
import { EditClientProfile } from '@/features/client/edit-client-profile';
import { EditContactProfile } from '@/features/client/edit-contact-profile';
import { QuickAddClient } from '@/features/client/quick-add-client';
import { SearchClientNavigation } from '@/features/client/search-client';
import { SaveServiceSheetHost } from '@/features/practice/save-service';
import { ScheduleSessionDialog } from '@/features/session/schedule-session';
import { Page } from '@/shared/ui/layout/page';
import { NavigationBar } from '@/shared/ui/navigation/navigation-bar';
import { ClientDetailsSheet, openClientDetailsSheet } from '@/widgets/client/client-details-sheet';
import { ContactDetailsSheet } from '@/widgets/client/contact-details-sheet';
import { ClientBillingSheet } from '@/widgets/ledger/client-billing-sheet';
import { CreateMenu } from '@/widgets/navigation/create-menu';
import { ShareReferralLinkTrigger } from '@/widgets/referral/share-link-trigger';
import { TrialBanner } from '@/widgets/subscription/trial-banner';
import { UserDropdown } from '@/widgets/user/user-dropdown';

import { SettingsLayoutBackToWorkspace } from './settings-layout/settings-layout-back-to-workspace';
import { SettingsLayoutSheet } from './settings-layout/settings-layout-sheet';
import { SettingsLayoutSidebar } from './settings-layout/settings-layout-sidebar';

const route = getRouteApi('/settings');

export function SettingsLayout() {
    // react compiler issue : isn't work correctly with useMatchRoute
    'use no memo';
    const isMobile = useBreakpointDown('md');
    const navigate = useNavigate();
    const matchRoute = useMatchRoute();
    const { hasEhrAccess } = route.useRouteContext();
    const { data: subscription } = useQuery(subscriptionMeOptions());
    const isExpired = isExpiredPaid(subscription);
    const isSettingsIndex = !!matchRoute({ to: '/settings', includeSearch: false });

    useEffect(() => {
        if (!isMobile && isSettingsIndex) {
            void navigate({ to: '/settings/profile', replace: true });
        }
    }, [isMobile, isSettingsIndex, navigate]);

    return (
        <Page>
            <NavigationBar
                leading={<SettingsLayoutBackToWorkspace />}
                actions={
                    <>
                        {!isExpired && (
                            <>
                                <SearchClientNavigation
                                    hasEhrAccess={hasEhrAccess}
                                    onSelectClient={openClientDetailsSheet}
                                />
                                <CreateMenu hasEhrAccess={hasEhrAccess} />
                            </>
                        )}
                        <ShareReferralLinkTrigger />
                    </>
                }
                dropdown={<UserDropdown />}
                banner={<TrialBanner />}
                mobileBanner={<TrialBanner variant="mobile" />}
            />
            <div className="flex flex-1 overflow-hidden">
                <aside
                    className={cn(
                        'shrink-0 overflow-y-auto',
                        isMobile ? 'w-full' : 'w-75 border-r border-border',
                    )}
                >
                    <SettingsLayoutSidebar />
                </aside>
                {!isMobile && (
                    <main className="flex flex-col flex-1 items-center overflow-y-auto">
                        <Outlet />
                    </main>
                )}
            </div>
            {isMobile && <SettingsLayoutSheet />}
            <QuickAddClient />
            <ScheduleSessionDialog />
            <SaveServiceSheetHost />
            <AddClient />
            <ClientDetailsSheet />
            <ContactDetailsSheet />
            <ClientBillingSheet />
            <EditClientProfile />
            <EditContactProfile />
            <AddClientContact />
        </Page>
    );
}
